import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { HelpCircle, Loader2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useConference } from '@/context/ConferenceContext';
import { getPagesByConference, getErrorMessage } from '@/services/api';
import { toast } from 'sonner';

const FAQPage = () => {
  const { shortName, importantDetails, loading: confLoading, siteConfig } = useConference();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFAQs = async () => {
      if (confLoading) return;

      setLoading(true);
      try {
        const pagesData = await getPagesByConference(shortName);
        if (pagesData && pagesData.faqs) {
          // Parse the FAQs JSON string
          const parsedFaqs = JSON.parse(pagesData.faqs);
          setFaqs(Array.isArray(parsedFaqs) ? parsedFaqs : []);
        } else {
          setFaqs([]);
        }
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
        toast.error(getErrorMessage(error));
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, [shortName, confLoading]);

  if (loading || confLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading FAQs...</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <Helmet>
        <title>FAQ - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Frequently asked questions about CCAI-2026" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="inline-flex w-16 h-16 bg-white rounded-2xl items-center justify-center mb-4 shadow-lg">
                <HelpCircle className="text-sky-500" size={32} />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Find answers to common questions about CCAI-2026
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQs */}
        <section className="section-spacing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <AccordionItem
                    value={faq.id}
                    className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm px-6"
                    data-testid={`faq-item-${faq.id}`}
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5">
                      <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Still Have Questions */}
        <section className="section-spacing bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-gray-600 mb-8">
              Can't find the answer you're looking for? Our team is here to help.
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-3 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-600 transition-colors"
              data-testid="contact-us-link"
            >
              Contact Us
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQPage;
